/**
 * SmartSaaS Color Palette
 *
 * Colors extracted from the web application's CSS variables
 * Both HSL and Hex formats provided for compatibility
 */

// Helper to convert HSL to Hex
function hslToHex(h: number, s: number, l: number): string {
	l /= 100;
	const a = (s * Math.min(l, 1 - l)) / 100;
	const f = (n: number) => {
		const k = (n + h / 30) % 12;
		const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
		return Math.round(255 * color)
			.toString(16)
			.padStart(2, '0');
	};
	return `#${f(0)}${f(8)}${f(4)}`;
}

// Light Theme Colors
export const lightColors = {
	// HSL format (for CSS/Tamagui)
	hsl: {
		background: 'hsl(210, 30%, 98%)',
		foreground: 'hsl(210, 40%, 12%)',
		card: 'hsl(0, 0%, 100%)',
		cardForeground: 'hsl(210, 40%, 12%)',
		popover: 'hsl(0, 0%, 100%)',
		popoverForeground: 'hsl(210, 40%, 12%)',
		primary: 'hsl(174, 72%, 45%)',
		primaryForeground: 'hsl(0, 0%, 100%)',
		primaryGlow: 'hsl(174, 72%, 60%)',
		secondary: 'hsl(210, 20%, 96%)',
		secondaryForeground: 'hsl(210, 40%, 12%)',
		muted: 'hsl(210, 20%, 94%)',
		mutedForeground: 'hsl(210, 15%, 45%)',
		accent: 'hsl(16, 85%, 60%)',
		accentForeground: 'hsl(0, 0%, 100%)',
		destructive: 'hsl(0, 84.2%, 60.2%)',
		destructiveForeground: 'hsl(210, 40%, 98%)',
		border: 'hsl(215, 16%, 90%)',
		input: 'hsl(210, 40%, 98%)',
		ring: 'hsl(174, 72%, 45%)',
	},
	// Hex format (for React Native/Tamagui)
	hex: {
		background: hslToHex(210, 30, 98),
		foreground: hslToHex(210, 40, 12),
		card: hslToHex(0, 0, 100),
		cardForeground: hslToHex(210, 40, 12),
		popover: hslToHex(0, 0, 100),
		popoverForeground: hslToHex(210, 40, 12),
		primary: hslToHex(174, 72, 45),
		primaryForeground: hslToHex(0, 0, 100),
		primaryGlow: hslToHex(174, 72, 60),
		secondary: hslToHex(210, 20, 96),
		secondaryForeground: hslToHex(210, 40, 12),
		muted: hslToHex(210, 20, 94),
		mutedForeground: hslToHex(210, 15, 45),
		accent: hslToHex(16, 85, 60),
		accentForeground: hslToHex(0, 0, 100),
		destructive: hslToHex(0, 84.2, 60.2),
		destructiveForeground: hslToHex(210, 40, 98),
		// Soft slate border aligned with SchemaComponent Field Type select
		border: '#e2e8f0',
		input: '#f8fafc',
		ring: hslToHex(174, 72, 45),
	},
};

// Dark Theme Colors
export const darkColors = {
	// HSL format
	hsl: {
		background: 'hsl(210, 40%, 6%)',
		foreground: 'hsl(210, 20%, 95%)',
		card: 'hsl(210, 35%, 10%)',
		cardForeground: 'hsl(210, 20%, 95%)',
		popover: 'hsl(210, 35%, 10%)',
		popoverForeground: 'hsl(210, 20%, 95%)',
		primary: 'hsl(174, 72%, 50%)',
		primaryForeground: 'hsl(0, 0%, 100%)',
		primaryGlow: 'hsl(174, 72%, 65%)',
		secondary: 'hsl(210, 30%, 15%)',
		secondaryForeground: 'hsl(210, 20%, 95%)',
		muted: 'hsl(210, 30%, 18%)',
		mutedForeground: 'hsl(210, 15%, 60%)',
		accent: 'hsl(16, 85%, 60%)',
		accentForeground: 'hsl(0, 0%, 100%)',
		destructive: 'hsl(0, 62.8%, 30.6%)',
		destructiveForeground: 'hsl(210, 40%, 98%)',
		border: 'hsla(213, 94%, 68%, 0.35)',
		input: 'hsla(220, 47%, 13%, 0.96)',
		ring: 'hsl(174, 72%, 50%)',
	},
	// Hex format
	hex: {
		background: hslToHex(210, 40, 6),
		foreground: hslToHex(210, 20, 95),
		card: hslToHex(210, 35, 10),
		cardForeground: hslToHex(210, 20, 95),
		popover: hslToHex(210, 35, 10),
		popoverForeground: hslToHex(210, 20, 95),
		primary: hslToHex(174, 72, 50),
		primaryForeground: hslToHex(0, 0, 100),
		primaryGlow: hslToHex(174, 72, 65),
		secondary: hslToHex(210, 30, 15),
		secondaryForeground: hslToHex(210, 20, 95),
		muted: hslToHex(210, 30, 18),
		mutedForeground: hslToHex(210, 15, 60),
		accent: hslToHex(16, 85, 60),
		accentForeground: hslToHex(0, 0, 100),
		destructive: hslToHex(0, 62.8, 30.6),
		destructiveForeground: hslToHex(210, 40, 98),
		// SchemaComponent dark select: blue-tinted border + deep slate input
		border: '#2a4a6e',
		input: '#121c32',
		ring: hslToHex(174, 72, 50),
	},
};

// Use hex colors for Tamagui (React Native compatibility)
export const tamaguiLightColors = lightColors.hex;
export const tamaguiDarkColors = darkColors.hex;
